import { useMemo } from 'react';

import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { useTaskManagementTask } from '../../api/use-task-management-task';
import type { AutomationAssessment } from '../automation-assessment/automation-assessment-model';
import type { ProfileTaskParentContext, ProfileTaskWorkflowStep } from '../model/profile-task';
import { mapProfileTaskAutomation, workflowStepIdAt } from './profile-task-automation-adapter';
import { useProfileTask } from './use-profile-task';

export type AutomationTileTaskAddress = {
    /** The job profile the task is listed on; absent for a work task opened on its own. */
    readonly context: ProfileTaskParentContext | undefined;
    readonly language: LanguageCodeForJSA | undefined;
    readonly taskId: string;
    readonly taskVersionId: string | undefined;
};

export type AutomationTileTask = {
    readonly assessment: AutomationAssessment | undefined;
    readonly title: string;
    readonly workflowSteps: readonly ProfileTaskWorkflowStep[];
};

export type UseAutomationTileTaskResult = {
    readonly data: AutomationTileTask | undefined;
    readonly isError: boolean;
    readonly isLoading: boolean;
};

/** The assessed task behind an automation tile, read from its job profile or, for a work task, from task management. */
export const useAutomationTileTask = ({
    context,
    language,
    taskId,
    taskVersionId,
}: AutomationTileTaskAddress): UseAutomationTileTaskResult => {
    const profileQuery = useProfileTask({
        architectureId: context?.architectureId ?? '',
        enabled: context !== undefined,
        language: language ?? 'en-US',
        parentId: context?.parentId ?? '',
        parentType: context?.parentType ?? 'role',
        parentVersion: context?.parentVersion,
        taskId,
        taskVersionId,
    });
    // Reads like the work task tile does, so the open tile's cached task is reused.
    const workTaskQuery = useTaskManagementTask({
        enabled: context === undefined,
        taskId,
        version: taskVersionId === undefined ? undefined : Number(taskVersionId),
    });
    const workTask = workTaskQuery.data;
    const workTaskData = useMemo(
        (): AutomationTileTask | undefined =>
            workTask === undefined
                ? undefined
                : {
                      assessment: mapProfileTaskAutomation(workTask).assessment,
                      title: workTask.title,
                      workflowSteps: (workTask.workflow?.steps ?? []).map((step, index) => ({
                          description: step.description,
                          title: step.title,
                          workflowStepId: workflowStepIdAt(index),
                      })),
                  },
        [workTask],
    );

    if (context === undefined) {
        return { data: workTaskData, isError: workTaskQuery.isError, isLoading: workTaskQuery.isLoading };
    }

    return {
        data:
            profileQuery.data === undefined
                ? undefined
                : {
                      assessment: profileQuery.automation?.assessment,
                      title: profileQuery.data.title,
                      workflowSteps: profileQuery.data.workflowSteps,
                  },
        isError: profileQuery.isError,
        isLoading: profileQuery.isLoading,
    };
};
